/**
 * Subtab "Asistente" (Sol 28/09/2026): la casa del Agente TA Metrics dentro de
 * la sección, para que quede claro que es solo de TA. Qué responde, preguntas
 * sugeridas y el botón para abrirlo. El chat sigue siendo el bot del costado
 * (supportIA en modo TA mientras la sección está montada), no otra pantalla:
 * cada botón abre el drawer, si corresponde con la pregunta ya cargada.
 */
import { currentLocale } from '@/lib/locale';
import { useTranslation } from 'react-i18next';
import { MessageSquareText, Settings2, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import taAgentAvatar from '@/assets/ta-agent-avatar.png';
import { openAssistant } from '@/lib/open-assistant';
import { useTaDashboard } from '@/hooks/useTalentAcquisition';
import { useTaSuggestedQuestions } from './ta-assistant-questions';

export function TaAssistantTab({ canAdmin, onGoToBotAdmin }: { canAdmin: boolean; onGoToBotAdmin: () => void }) {
  const { t } = useTranslation();
  const preguntas = useTaSuggestedQuestions();
  const k = 'talent_acquisition.assistant_tab';
  // Ya está en cache si la persona pasó por el Dashboard; si no, la trae (misma query)
  const dashboard = useTaDashboard(false, true);
  const downloadedAt = dashboard.data?.downloadedAt
    ? new Date(dashboard.data.downloadedAt).toLocaleString(currentLocale(), { dateStyle: 'short', timeStyle: 'short' })
    : null;

  return (
    <div className="max-w-4xl space-y-4">
      <Card>
        <CardContent className="flex flex-wrap items-center gap-4 pt-6">
          <img src={taAgentAvatar} alt={t('assistant.assistantchatdrawer.ta_name')} className="h-16 w-16 rounded-full object-cover" />
          <div className="min-w-[240px] flex-1">
            <h2 className="text-xl font-bold tracking-tight">{t('assistant.assistantchatdrawer.ta_name')}</h2>
            <p className="text-sm text-muted-foreground">{t('talent_acquisition.assistant.intro')}</p>
            {downloadedAt && (
              <p className="mt-1 text-xs text-muted-foreground">
                {t(`${k}.base_line`, { rows: dashboard.data?.filas ?? 0, at: downloadedAt })}
              </p>
            )}
          </div>
          <Button className="gap-2" onClick={() => openAssistant()}>
            <MessageSquareText className="h-4 w-4" /> {t(`${k}.open`)}
          </Button>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t(`${k}.suggested_title`)}</CardTitle>
            <CardDescription>{t(`${k}.suggested_desc`)}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {preguntas.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => openAssistant(q)}
                className="w-full rounded-lg border border-muted-foreground/20 px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted"
              >
                {q}
              </button>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t(`${k}.how_title`)}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <ul className="list-disc space-y-1.5 pl-5 text-muted-foreground">
              <li>{t(`${k}.how_1`)}</li>
              <li>{t(`${k}.how_2`)}</li>
              <li>{t(`${k}.how_3`)}</li>
            </ul>
            <p className="flex items-start gap-2 text-xs text-muted-foreground">
              <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {t(`${k}.privacy_note`)}
            </p>
            {canAdmin && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={onGoToBotAdmin}>
                <Settings2 className="h-3.5 w-3.5" /> {t(`${k}.admin_hint`)}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
